import { useCallback, useEffect, useState } from 'react';
import _uniqBy from 'lodash/uniqBy';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { CSS_VARS } from '@/constants/css-vars';
import { ICONS } from '@/constants/icons';
import {
  BUSINESS_DETAILS_SECTION_ID,
  BUSINESS_DETAILS_SECTION_TITLE,
} from '@pages/Facilities/Sections/Details/types';
import { SOFTWARE_LICENSE_PAGE_ID } from '@pages/Facilities/Sections/Details/SoftwareLicense/data';
import {
  LOCATION_DOCS_SECTION_ID,
  LOCATION_DOCS_PAGE_ID,
  LOCATION_DOCS_SECTION_TITLE,
  LOCATION_DOCS_PAGE_TITLE,
  facilityDocuments,
} from '@pages/Facilities/Sections/LocationDocs/data';

import InputText from '../Forms/InputText';
import UploadDocumentFile, {
  DocumentInfo,
} from '@/features/UploadDocumentFile';
import ToastMessage from '@/components/UI/ToastMessage';

import { useMultipleProfiles } from '@/store/useMultipleProfiles';
import { useCheckTier1WithoutLicense } from '@/utils/hooks/useCheckTier1WithoutLicense';
import useGetBusinessFacilityInformation from '@/api/hooks/useGetBusinessFacilityInformation';
import useUpdateFacilityInformation from '@/api/hooks/useUpdateFacilityInformation';
import useCompleteLicenseForTier1 from '@/api/hooks/useCompleteLicenseForTier1';
import { getFacilityDocumentItem } from '@pages/Facilities/Sections/facilityUtility';
import { LayoutAreas } from '@/types/utils';

import type {
  ItemDto,
  SectionDto,
} from '@/api/hooks/useUpdateFacilityInformation/types';

export const useLicenseRequireBanner = ({
  layoutArea,
}: {
  layoutArea?: LayoutAreas;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bannerHeight = isMobile ? 7 : 5;

  const [isDisplayBanner, setIsDisplayBanner] = useState(false);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [licenseNumber, setLicenseNumber] = useState<string>();
  const [documentLicenseInfo, setLicenseDocumentInfo] = useState<DocumentInfo>({
    isUploaded: false,
    businessDocument: undefined,
  });

  const userProfile = useMultipleProfiles((state) => state.userProfile);
  const facilityId = userProfile?.SystemUser?.facilities?.[0]?.id;
  const businessId = userProfile?.businesses?.[0]?.id;
  const { noEligibleLicense, reCheckTier1WithoutLicense } =
    useCheckTier1WithoutLicense();

  const updateFacilityInformation = useUpdateFacilityInformation();
  const completeLicenseForTier1 = useCompleteLicenseForTier1();

  const { data: businessFacilityInformation } =
    useGetBusinessFacilityInformation(
      businessId ?? '',
      facilityId ?? '',
      BUSINESS_DETAILS_SECTION_ID,
      SOFTWARE_LICENSE_PAGE_ID,
      {
        enabled: businessId != null && facilityId != null,
      },
    );

  const { data: locationDocs } = useGetBusinessFacilityInformation(
    businessId ?? '',
    facilityId ?? '',
    LOCATION_DOCS_SECTION_ID,
    LOCATION_DOCS_PAGE_ID,
    { enabled: businessId != null && facilityId != null },
  );

  useEffect(() => {
    if (noEligibleLicense && layoutArea !== LayoutAreas.ONBOARDING) {
      setIsDisplayBanner(true);
    }
  }, [layoutArea, noEligibleLicense]);

  const calculateHeaderPaddingTop = useCallback(
    (defaultPaddingTop: number) =>
      isDisplayBanner ? defaultPaddingTop + bannerHeight : defaultPaddingTop,
    [bannerHeight, isDisplayBanner],
  );

  const closeBanner = useCallback(() => {
    setIsDisplayBanner(false);
  }, []);

  const handleUpdateDocument = async () => {
    if (businessId == null || facilityId == null) return;

    const previousDocument = (locationDocs?.page.items || []).filter(
      (item) => item.id !== facilityDocuments.STATE_ISSUED_MARIJUANA_LICENSE.id,
    );
    const licenseDocument = getFacilityDocumentItem(
      facilityDocuments.STATE_ISSUED_MARIJUANA_LICENSE.label,
      facilityDocuments.STATE_ISSUED_MARIJUANA_LICENSE.id,
      documentLicenseInfo.businessDocument?.syncteraDocumentId ?? '',
    );

    const storedDocuments = [...(previousDocument ?? []), licenseDocument];

    const finalStoredDocuments = _uniqBy(storedDocuments, 'id');

    try {
      await updateFacilityInformation.mutateAsync({
        businessId,
        facilityId,
        section: {
          id: LOCATION_DOCS_SECTION_ID,
          title: LOCATION_DOCS_SECTION_TITLE,
          page: {
            id: LOCATION_DOCS_PAGE_ID,
            title: LOCATION_DOCS_PAGE_TITLE,
            items: finalStoredDocuments,
          },
        },
      });
    } catch (error) {
      console.error('Error updating documents:', error);
    }
  };

  const handleSubmitLicense = useCallback(async () => {
    if (businessId == null || facilityId == null) return;
    setIsSubmitting(true);

    const existingItems = businessFacilityInformation?.page?.items || [];
    const filteredItems = existingItems.filter(
      (item) => item.id !== 'license-number',
    );

    const updatedItems: ItemDto[] = [
      ...filteredItems,
      {
        id: 'license-number',
        type: 'QUESTION',
        question: 'License Number',
        answer: licenseNumber ?? '',
      },
    ];

    const sectionData: SectionDto = {
      id: BUSINESS_DETAILS_SECTION_ID,
      title: BUSINESS_DETAILS_SECTION_TITLE,
      page: {
        id: SOFTWARE_LICENSE_PAGE_ID,
        title: 'Business Detail Section - Cannabis License',
        items: updatedItems,
      },
    };

    await updateFacilityInformation.mutateAsync(
      {
        businessId,
        facilityId,
        section: sectionData,
      },
      {
        async onSuccess() {
          try {
            await handleUpdateDocument();
            await completeLicenseForTier1.mutateAsync({
              businessId,
              facilityId,
              licenseNumber: licenseNumber ?? '',
            });
            ToastMessage.success('License number updated successfully');
          } catch (error) {
            console.error('Error completing license update:', error);
            ToastMessage.error(
              'An error occurred while completing license setup',
            );
          } finally {
            onCloseModal();
            closeBanner();
            void reCheckTier1WithoutLicense();
          }
        },
        onError(error) {
          console.error('Error updating license number:', error);
          ToastMessage.error('Have an error updating license number');
          onCloseModal();
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseNumber]);

  const onCloseModal = () => {
    setIsOpenUploadModal(false);
    setIsSubmitting(false);
    setLicenseNumber('');
  };

  const renderUploadModal = useCallback(() => {
    return (
      <Dialog
        onClose={onCloseModal}
        open={isOpenUploadModal}
        fullWidth
        maxWidth={'sm'}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h5">
            {'Upload State-Issued Marijuana License'}
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <InputText
                label="Cannabis License"
                value={licenseNumber}
                onChange={(e) => {
                  setLicenseNumber(e.target.value);
                }}
                name="cannabis_license"
                required
                width={'100%'}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography display="inline" fontWeight={600} variant="body2">
                Please upload your license.
              </Typography>
              <Box marginTop={'-2rem'}>
                <UploadDocumentFile
                  facilityId={facilityId}
                  isRequired
                  label="State-Issued Marijuana License"
                  type="state_issued_marijuana_license"
                  setDocumentInfo={setLicenseDocumentInfo}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: '16px' }}>
          <Button
            onClick={onCloseModal}
            disabled={isSubmitting}
            sx={{ minWidth: '110px' }}
          >
            Cancel
          </Button>
          <Button
            disabled={
              isSubmitting ||
              !documentLicenseInfo?.businessDocument?.syncteraDocumentId ||
              !licenseNumber
            }
            variant="contained"
            sx={{ minWidth: '110px' }}
            onClick={handleSubmitLicense}
          >
            {isSubmitting && (
              <CircularProgress color="inherit" size={14} sx={{ mr: '2px' }} />
            )}
            <span>Save</span>
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [
    documentLicenseInfo?.businessDocument?.syncteraDocumentId,
    facilityId,
    isOpenUploadModal,
    isSubmitting,
    licenseNumber,
    handleSubmitLicense,
  ]);

  const renderBanner = useCallback(() => {
    if (!isDisplayBanner) return null;

    return (
      <>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: `${bannerHeight}rem`,
            backgroundColor: `var(${CSS_VARS.Colors.BrandBlue[400]})`,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px 0 16px',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              color={`var(${CSS_VARS.Colors.White})`}
              fontSize={14}
              fontWeight={600}
            >
              Please upload your State-Issued Marijuana License to receive full
              access to the Banking Portal.
            </Typography>
            <Button
              sx={{
                backgroundColor: '#F5861F',
                color: '#000',
                '&:hover': {
                  opacity: 0.9,
                  backgroundColor: '#F5861F',
                },
              }}
              size="small"
              variant="contained"
              onClick={() => {
                setIsOpenUploadModal(true);
              }}
            >
              Upload
            </Button>
          </Box>
          <Box
            sx={{
              width: '24px',
            }}
          >
            <ICONS.Close
              onClick={closeBanner}
              cursor="pointer"
              size="24px"
              color={`var(${CSS_VARS.Colors.White})`}
            />
          </Box>
        </Box>
        {renderUploadModal()}
      </>
    );
  }, [bannerHeight, isDisplayBanner, closeBanner, renderUploadModal]);

  return {
    isDisplayBanner,
    renderBanner,
    calculateHeaderPaddingTop,
  };
};
